import { useCallback, useEffect, useMemo } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkDeleteSeatSaversDialog } from 'src/dialogs/BulkEdits/BulkDeleteSeatSaversDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserCanManageSeatSaver } from 'src/hooks/useUserHasListingPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  Feature,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_DELETE_SEAT_SAVERS_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchDeleteSeatSavers } from './LaunchDeleteSeatSavers';

export const useBulkDeleteSeatSavers = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasManageSeatSaverPermission = useUserCanManageSeatSaver();
  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext();

  const updateKey = event
    ? `inventory-delete-seat-savers-event-${event.virtId}`
    : INVENTORY_BULK_DELETE_SEAT_SAVERS_UPDATE_KEY;

  const {
    refreshCatalog,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkDeleteSeatSavers,
    closeDialog: closeBulkDeleteSeatSavers,
  } = useDialog(DialogId.BulkDeleteSeatSeavers, BulkDeleteSeatSaversDialog);

  const onSubmitDeleteSeatSavers = useCallback(
    async (
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      const client = new BulkEditListingClient(activeAccountWebClientConfig);
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await client.bulkDeleteSeatSaversPreview(
              filterQueryWithEventIds
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkDeleteSeatSaversPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await client.bulkDeleteSeatSavers(
              preview!.preview,
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Because delete listings can result in an empty event - so we need to reget catalog
                await refreshCatalog();

                setActivePosEntity(0);
                await refreshExpandedListItems();
              }
              closeBulkDeleteSeatSavers();
            }
          },
          (error) => {
            showErrorDialog('onSubmitDeleteSeatSavers', error, {
              trackErrorData: { preview, event },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkDeleteSeatSavers,
      event,
      filterQueryWithEventIds,
      refreshCatalog,
      refreshExpandedListItems,
      setActivePosEntity,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkDeleteSeatSavers({
      updateKey,
      onOkay: onSubmitDeleteSeatSavers,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkDeleteSeatSavers,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditDeleteSeatSavers,
        onSelect: openDialogStableRef,
      },
      disabled: !hasManageSeatSaverPermission || !hasSeatSaverFeature,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditDeleteSeatSavers,
        disabled: !hasManageSeatSaverPermission || !hasSeatSaverFeature,
      });
    };
  }, [
    hasManageSeatSaverPermission,
    hasSeatSaverFeature,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkDeleteSeatSavers({
        updateKey,
        onOkay: onSubmitDeleteSeatSavers,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkDeleteSeatSavers,
      });
    },
    [
      closeBulkDeleteSeatSavers,
      onSubmitDeleteSeatSavers,
      openBulkDeleteSeatSavers,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasManageSeatSaverPermission || !hasSeatSaverFeature) {
      return [];
    }
    return [
      {
        title: ContentId.DeletePlaceholders,
        disabled: isLoading,
        onClick,
      },
    ];
  }, [hasManageSeatSaverPermission, hasSeatSaverFeature, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchDeleteSeatSavers
        key="onSubmitDeleteSeatSavers"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
